import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import saleService from '../services/saleService';
import { getCountryName } from '../utils/countries';
import { formatDateToMadridReadable } from '../utils/dateUtils';

const SaleDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadSale = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await saleService.getSaleById(id, { include: ['GoogleAdsConversion', 'Product'] });
        setSale(data);
      } catch (err) {
        console.error('Error al cargar la venta:', err);
        setError('No pudimos cargar esta venta.');
      } finally {
        setLoading(false);
      }
    };

    loadSale();
  }, [id]);

  const handleDelete = async () => {
    const confirmed = window.confirm(
      `Vas a eliminar la venta #${id}. Esta accion no se puede deshacer.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await saleService.deleteSale(id);
      navigate('/sales');
    } catch (err) {
      console.error('Error al eliminar la venta:', err);
      setError('No pudimos eliminar esta venta.');
    }
  };

  const formatOriginalCurrency = (amount, currency) => {
    if (amount === null || amount === undefined || amount === '') {
      return '-';
    }

    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(amount);
    } catch (err) {
      return `${amount} ${currency || 'EUR'}`;
    }
  };

  const formatDateValue = (value) => {
    if (!value) {
      return '-';
    }

    return formatDateToMadridReadable(value);
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Aprobada';
      case 'rejected':
        return 'Cancelada';
      default:
        return 'Pendiente de validar';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'approved':
        return 'badge-success';
      case 'rejected':
        return 'badge-error';
      default:
        return 'badge-info';
    }
  };

  const getSourceText = (source) => {
    switch (source) {
      case 'api':
        return 'Importada por API';
      case 'manual':
        return 'Creada manualmente';
      case 'bulk':
        return 'Importacion masiva';
      default:
        return source || '-';
    }
  };

  const getSourceClass = (source) => {
    switch (source) {
      case 'api':
        return 'badge-info';
      case 'manual':
        return 'badge-warning';
      case 'bulk':
        return 'badge-neutral';
      default:
        return 'badge-ghost';
    }
  };

  const renderField = (label, value, options = {}) => {
    const valueClassName = options.mono
      ? 'sale-detail-value sale-detail-value-mono'
      : 'sale-detail-value';
    const displayValue = value === null || value === undefined || value === '' ? '-' : value;

    return (
      <div className="sale-detail-item" key={label}>
        <div className="sale-detail-label">{label}</div>
        <div className={valueClassName}>{displayValue}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="sale-detail-shell">
        <div className="premium-panel sale-detail-empty">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Cargando la venta y su contexto...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sale-detail-shell">
        <div className="page-header">
          <div>
            <p className="page-eyebrow">Ventas</p>
            <h1 className="page-title">No pudimos abrir esta venta</h1>
            <p className="page-subtitle">
              Vuelve al listado para revisar otra venta o reintenta la carga.
            </p>
          </div>
          <div className="page-actions">
            <Link to="/sales" className="btn btn-outline">
              Volver a ventas
            </Link>
          </div>
        </div>

        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="sale-detail-shell">
        <div className="page-header">
          <div>
            <p className="page-eyebrow">Ventas</p>
            <h1 className="page-title">Esta venta ya no esta disponible</h1>
            <p className="page-subtitle">
              No encontramos una venta con el identificador #{id}.
            </p>
          </div>
          <div className="page-actions">
            <Link to="/sales" className="btn btn-outline">
              Volver a ventas
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const saleTitle = sale.Product?.name || sale.productName || `Venta #${sale.id}`;
  const saleSubtitleParts = [
    sale.orderId ? `Pedido ${sale.orderId}` : 'Sin pedido asociado',
    formatDateValue(sale.saleDate),
  ].filter(Boolean);

  const summaryCards = [
    {
      label: 'Estado',
      value: <span className={`badge ${getStatusClass(sale.status)}`}>{getStatusText(sale.status)}</span>,
    },
    {
      label: 'Origen',
      value: <span className={`badge ${getSourceClass(sale.source)}`}>{getSourceText(sale.source)}</span>,
    },
    {
      label: 'Importe',
      value: formatOriginalCurrency(sale.amount, sale.currency),
    },
    {
      label: 'Comision',
      value: formatOriginalCurrency(sale.commission, sale.currency),
    },
  ];

  const operationFields = [
    renderField(
      'Producto',
      sale.Product ? (
        <Link to={`/products/${sale.Product.id}`} className="link link-primary">
          {sale.Product.name}
        </Link>
      ) : (
        sale.productName || '-'
      ),
    ),
    renderField(
      'Plataforma',
      <span className="badge badge-outline" style={{ '--badge-color': sale.Platform?.color || '#808080' }}>
        {sale.Platform?.name || 'Sin plataforma'}
      </span>,
    ),
    renderField(
      'Pedido en plataforma',
      sale.orderId || '-',
      { mono: true }
    ),
    renderField(
      'GCLID guardado en la venta',
      sale.gclid || '-',
      { mono: true }
    ),
  ];

  const customerAndTimelineFields = [
    renderField('Fecha de venta', formatDateValue(sale.saleDate)),
    renderField(
      'Fecha de ejecucion',
      formatDateValue(sale.executionDate),
    ),
    renderField('Registrada en el sistema', formatDateValue(sale.createdAt)),
    renderField('Ultima actualizacion', formatDateValue(sale.updatedAt)),
    renderField('Viajeros', sale.travelers || '-'),
    renderField(
      'Tiempo de conversion',
      sale.conversionTime || '-',
    ),
    renderField(
      'ID del contacto',
      sale.customerId || '-',
      { mono: true }
    ),
    renderField('Pais del cliente', getCountryName(sale.customerCountry) || '-'),
  ];

  const financeFields = [
    renderField(
      'Importe reportado',
      formatOriginalCurrency(sale.amount, sale.currency),
    ),
    renderField(
      'Comision reportada',
      formatOriginalCurrency(sale.commission, sale.currency),
    ),
    renderField(
      'Tipo de cambio usado',
      sale.exchangeRate ? `1 ${sale.currency || 'EUR'} = ${sale.exchangeRate} EUR` : '-',
      { mono: true }
    ),
  ];

  const marketingFields = [
    renderField('Campana', sale.campaign || '-'),
    renderField(
      'Articulo atribuido',
      sale.articleId ? (
        <Link to={`/articles/${sale.articleId}`} className="link link-primary">
          {sale.article || `Articulo #${sale.articleId}`}
        </Link>
      ) : (
        sale.article || '-'
      ),
    ),
    renderField('ID del articulo', sale.articleId || '-', { mono: true }),
  ];

  const originalValuesVisible = sale.originalAmount || sale.originalCommission;
  const bundleVisible = sale.isBundle || sale.isBundleProduct;

  return (
    <div className="sale-detail-shell">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Venta #{sale.id}</p>
          <h1 className="page-title">{saleTitle}</h1>
          <p className="page-subtitle">{saleSubtitleParts.join(' - ')}</p>
        </div>
        <div className="page-actions">
          <Link to="/sales" className="btn btn-outline">
            Volver a ventas
          </Link>
          <Link to={`/sales/edit/${id}`} className="btn btn-primary">
            Editar venta
          </Link>
          <button onClick={handleDelete} className="btn btn-error">
            Eliminar venta
          </button>
        </div>
      </div>

      <div className="sale-summary-grid">
        {summaryCards.map((card) => (
          <div className="premium-panel sale-summary-card" key={card.label}>
            <div className="sale-summary-label">{card.label}</div>
            <div className="sale-summary-value">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="sale-detail-grid">
        <section className="premium-panel sale-detail-section">
          <div className="panel-header">
            <p className="panel-kicker">Operacion</p>
            <h2 className="panel-title">Lo esencial</h2>
          </div>
          <div className="sale-detail-list">{operationFields}</div>
        </section>

        <section className="premium-panel sale-detail-section">
          <div className="panel-header">
            <p className="panel-kicker">Cliente y Fechas</p>
            <h2 className="panel-title">Contexto operativo</h2>
          </div>
          <div className="sale-detail-list">{customerAndTimelineFields}</div>
        </section>

        <section className="premium-panel sale-detail-section">
          <div className="panel-header">
            <p className="panel-kicker">Importes</p>
            <h2 className="panel-title">Lectura financiera</h2>
          </div>
          <div className="sale-detail-list">{financeFields}</div>

          {originalValuesVisible ? (
            <div className="sale-detail-note-box">
              <div className="sale-detail-note-box-title">Valores originales</div>
              {sale.originalAmount ? (
                <p className="sale-detail-note-box-copy">
                  Monto original: <strong>{formatOriginalCurrency(sale.originalAmount, sale.currency)}</strong>
                </p>
              ) : null}
              {sale.originalCommission ? (
                <p className="sale-detail-note-box-copy">
                  Comision original: <strong>{formatOriginalCurrency(sale.originalCommission, sale.currency)}</strong>
                </p>
              ) : null}
            </div>
          ) : null}

          {bundleVisible ? (
            <div className="sale-detail-note-box sale-detail-note-box-info">
              <div className="sale-detail-note-box-title">Bundle</div>
              {sale.isBundle ? (
                <p className="sale-detail-note-box-copy">
                  Esta venta actua como <strong>bundle principal</strong>.
                </p>
              ) : null}
              {sale.isBundleProduct ? (
                <p className="sale-detail-note-box-copy">
                  Esta venta pertenece a un <strong>producto de bundle</strong>.
                </p>
              ) : null}
              {sale.bundleId ? (
                <p className="sale-detail-note-box-copy">
                  ID de bundle: <strong>{sale.bundleId}</strong>
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      <div className="sale-detail-split">
        <section className="premium-panel sale-detail-section">
          <div className="panel-header">
            <p className="panel-kicker">Marketing</p>
            <h2 className="panel-title">Atribucion y contenido</h2>
          </div>
          <div className="sale-detail-list">{marketingFields}</div>
        </section>

        <section className="premium-panel sale-detail-section">
          <div className="panel-header">
            <p className="panel-kicker">Notas</p>
            <h2 className="panel-title">Contexto interno</h2>
          </div>
          <div className="sale-detail-notes">
            {sale.notes ? sale.notes : 'No hay notas internas para esta venta.'}
          </div>
        </section>
      </div>

      <section className="premium-panel sale-detail-section">
        <div className="panel-header">
          <p className="panel-kicker">Google Ads</p>
          <h2 className="panel-title">Conversion asociada</h2>
        </div>

        {sale.GoogleAdsConversion ? (
          <div className="sale-detail-list">
            {renderField(
              'GCLID de la conversion',
              sale.GoogleAdsConversion.gclid || '-',
              { mono: true }
            )}
            {renderField(
              'Detalle de conversion',
              <Link to={`/conversions/${sale.GoogleAdsConversion.id}`} className="btn btn-primary btn-sm">
                Ver conversion en detalle
              </Link>
            )}
          </div>
        ) : (
          <div className="sale-detail-empty-note">
            Esta venta no tiene una conversion de Google Ads enlazada.
          </div>
        )}
      </section>
    </div>
  );
};

export default SaleDetailScreen;
